/**
 * The receipt logo: a picture turned into the exact dots a 58 mm thermal printer prints (ADR-0013).
 * The phone sends the bits to the printer as they are, so what the owner previews here is what prints.
 *
 * Format (the server validates it, services/api/app/modules/business/logo.py): `width` x `height` dots,
 * rows from the top, `width / 8` bytes per row, leftmost dot in the most significant bit, 1 = black.
 */

/** 58 mm paper prints 384 dots across; a taller logo wastes paper and Bluetooth time. */
export const MAX_LOGO_WIDTH = 384
export const MAX_LOGO_HEIGHT = 200

export type LogoMode = 'sharp' | 'photo'

export type Mono = { width: number; height: number; bits: Uint8Array }

/** Where the picture lands: as large as fits the paper without distortion, never enlarged. */
export function fitSize(srcWidth: number, srcHeight: number): { width: number; height: number } {
  const scale = Math.min(1, MAX_LOGO_WIDTH / srcWidth, MAX_LOGO_HEIGHT / srcHeight)
  return { width: Math.max(1, Math.round(srcWidth * scale)), height: Math.max(1, Math.round(srcHeight * scale)) }
}

/** The dot width the printer needs: whole bytes per row. Never below 8 dots, the server's minimum. */
export const paddedWidth = (width: number): number => Math.max(8, Math.ceil(width / 8) * 8)

/**
 * RGBA pixels (already scaled to the size they will print, with transparency flattened onto white by
 * the caller) to a 1-bit bitmap.
 *
 * - `sharp`: black or white at 50% brightness, for logos and lettering.
 * - `photo`: Floyd–Steinberg dithering, for pictures with shades.
 * - `invert` turns a white logo on a dark background into black dots on white paper.
 *
 * The result is padded on the right with white to a whole number of bytes, and to at least 8 x 8.
 */
export function toMono(rgba: Uint8ClampedArray, width: number, height: number, mode: LogoMode, invert: boolean): Mono {
  if (rgba.length !== width * height * 4) throw new Error('pixel data does not match the size')
  const outWidth = paddedWidth(width)
  const outHeight = Math.max(8, height)
  // Brightness 0 (black) to 255 (white); padding is white.
  const gray = new Float32Array(outWidth * outHeight).fill(255)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const a = (rgba[i + 3] ?? 255) / 255
      const lum = 0.299 * (rgba[i] ?? 0) + 0.587 * (rgba[i + 1] ?? 0) + 0.114 * (rgba[i + 2] ?? 0)
      const flattened = lum * a + 255 * (1 - a) // transparent shows the white paper
      gray[y * outWidth + x] = invert ? 255 - flattened : flattened
    }
  }

  const bits = new Uint8Array((outWidth / 8) * outHeight)
  const black = (x: number, y: number) => {
    bits[y * (outWidth / 8) + (x >> 3)]! |= 0x80 >> (x & 7)
  }
  if (mode === 'sharp') {
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) if (gray[y * outWidth + x]! < 128) black(x, y)
  } else {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const at = y * outWidth + x
        const old = gray[at]!
        const next = old < 128 ? 0 : 255
        if (next === 0) black(x, y)
        const err = old - next
        if (x + 1 < width) gray[at + 1]! += (err * 7) / 16
        if (y + 1 < height) {
          if (x > 0) gray[at + outWidth - 1]! += (err * 3) / 16
          gray[at + outWidth]! += (err * 5) / 16
          if (x + 1 < width) gray[at + outWidth + 1]! += (err * 1) / 16
        }
      }
    }
  }
  return { width: outWidth, height: outHeight, bits }
}

/** True when nothing would print: the server refuses a blank logo too. */
export const isBlank = (m: Mono): boolean => m.bits.every((b) => b === 0)

export function toBase64(bits: Uint8Array): string {
  let s = ''
  for (const b of bits) s += String.fromCharCode(b)
  return btoa(s)
}

export function fromBase64(data: string): Uint8Array {
  const s = atob(data)
  return Uint8Array.from(s, (c) => c.charCodeAt(0))
}

/** The body of `PUT /v1/business/logo`. */
export const toPayload = (m: Mono) => ({ width: m.width, height: m.height, data: toBase64(m.bits) })

/** Black dots as RGBA, for drawing a preview. */
export function toRgba(m: Mono): Uint8ClampedArray {
  const out = new Uint8ClampedArray(m.width * m.height * 4)
  for (let y = 0; y < m.height; y++) {
    for (let x = 0; x < m.width; x++) {
      const on = (m.bits[y * (m.width / 8) + (x >> 3)]! & (0x80 >> (x & 7))) !== 0
      const i = (y * m.width + x) * 4
      out[i] = out[i + 1] = out[i + 2] = on ? 0 : 255
      out[i + 3] = 255
    }
  }
  return out
}

/** A picture file as RGBA at print size, flattened onto white. Browser only (canvas); tests inject a stub. */
export type DecodedPicture = { rgba: Uint8ClampedArray; width: number; height: number }
export type Decoder = (file: File) => Promise<DecodedPicture>

export const decodePicture: Decoder = async (file) => {
  const bitmap = await createImageBitmap(file)
  try {
    const { width, height } = fitSize(bitmap.width, bitmap.height)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('no canvas')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, width, height)
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bitmap, 0, 0, width, height)
    return { rgba: ctx.getImageData(0, 0, width, height).data, width, height }
  } finally {
    bitmap.close()
  }
}
